import { useState, useEffect, useCallback } from "react";
import { useCurrentLang } from "@/i18n/language";

export interface GalleryImage {
  key: string;
  thumb: string;
  full: string;
  caption: string;
  captionEn?: string;
  /** Reservierter Platz, dessen echtes Foto noch hochgeladen werden muss. */
  placeholder?: boolean;
}

interface GalleryGridProps {
  images: GalleryImage[];
}

export default function GalleryGrid({ images }: GalleryGridProps) {
  const lang = useCurrentLang();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxOpen, goPrev, goNext]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {images.map((img, idx) => {
          if (img.placeholder) {
            return (
              <div
                key={idx}
                className="relative rounded-lg overflow-hidden border border-dashed border-background-300 text-left"
              >
                <div className="aspect-[4/3] bg-background-100 flex flex-col items-center justify-center gap-2 text-foreground-400">
                  <i className="ri-image-2-line text-3xl"></i>
                  <span className="text-[11px] font-medium uppercase tracking-[0.2em]">
                    {lang === "en" ? "Photo coming soon" : "Foto folgt"}
                  </span>
                </div>
                <div className="px-3 py-2 bg-background-50 border-t border-background-200">
                  <p className="text-xs text-foreground-700 font-medium truncate">{img.caption}</p>
                </div>
              </div>
            );
          }
          return (
            <button
              key={idx}
              type="button"
              onClick={() => openLightbox(idx)}
              className="group relative rounded-lg overflow-hidden border border-background-200 cursor-pointer text-left"
            >
              <div className="aspect-[4/3]">
                <img
                  src={img.thumb}
                  alt={img.caption}
                  onError={(e) => {
                    const el = e.currentTarget;
                    if (el.src !== img.full) el.src = img.full;
                  }}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity w-10 h-10 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-900">
                  <i className="ri-search-line text-lg"></i>
                </span>
              </div>
              <div className="px-3 py-2 bg-background-50 border-t border-background-200">
                <p className="text-xs text-foreground-700 font-medium truncate">{img.caption}</p>
              </div>
            </button>
          );
        })}
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 shadow-lg cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>

          <div className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[currentIndex].full}
              alt={images[currentIndex].caption}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-white text-sm text-center mt-3">
              {images[currentIndex].caption}
              <span className="text-white/60 ml-2">
                ({currentIndex + 1} / {images.length})
              </span>
            </p>
          </div>
        </div>
      )}
    </>
  );
}